import React, { useEffect, useRef, useState } from "react";

import { Button, Dropdown, Form, Icon, Input, Label } from "semantic-ui-react";

import OfficialAccountAddressForm from "HIS/IsHealth/OfficialAccountAddressForm";
import QRCode from "react-qr-code";
import BusinessInformationForm from "HIS/IsHealth/BusinessInformationForm";
import AccountingInformationForm from "HIS/IsHealth/AccountingInformationForm"
import AccountingAddressForm from "HIS/IsHealth/AccountingAddressForm";

import { copyToClipboard } from "react-lib/apps/HISV3/common/CommonInterface";

type SettingOAAccountFormDetailedProps = {
  data: Record<string, any>;
  error?: Record<string, any>;
  readOnly?: boolean;
  masterData?: any;
  masterOptions?: any;
  healthFacilityTypeOptions?: Record<string, any>[];
  onChangeValue?: (e: any, data: any) => void;
  onChangeLogoImage?: (file: File, fileUrl: string) => void;
  languageUX?: "en" | "th";
};

const IMAGES = {
  pencil: "/static/images/pencil-edit-icon.png",
};

const QR_SIZE = 105;
const QR_DOWNLOAD_SIZE = 900;

const SettingOAAccountFormDetailed: React.FC<SettingOAAccountFormDetailedProps> = (props) => {
  const inputRef = useRef<any>(null);
  const qrRef = useRef<HTMLDivElement>(null);
  const [isCopied, setIsCopied] = useState(false);

  const handleCopyClipboard = async () => {
    const textToCopy = inputRef.current?.inputRef?.current?.value;

    if (textToCopy) {
      const success = await copyToClipboard(textToCopy);

      if (success) {
        setIsCopied(true);
        // Reset the copied state after 2 seconds
        setTimeout(() => {
          setIsCopied(false);
        }, 2000);
      }
    }
  };

  const downloadQR = () => {
    const svg = qrRef.current?.querySelector("svg");

    if (!svg) {
      return;
    }

    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const img = new Image();

    canvas.width = QR_DOWNLOAD_SIZE;
    canvas.height = QR_DOWNLOAD_SIZE;

    img.onload = () => {
      if (!ctx) {
        return;
      }

      ctx.fillStyle = "white";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, QR_DOWNLOAD_SIZE, QR_DOWNLOAD_SIZE);

      const pngFile = canvas.toDataURL("image/png");
      const downloadLink = document.createElement("a");

      downloadLink.download = "qrcode.png";
      downloadLink.href = pngFile;
      downloadLink.click();
    };

    img.src = `data:image/svg+xml;base64,${btoa(decodeURIComponent(encodeURIComponent(svgData)))}`;
  };

  const requiredTextError = (isRequired: boolean) => {
    if (isRequired)
      return (
        <label style={{ color: "red", fontSize: "12px", fontWeight: "normal" }}>
          <span style={{ color: "red" }}>*</span>
          {props.languageUX === "en"
            ? "Please complete all required fields."
            : "กรุณาระบุข้อมูลให้ครบถ้วนค่ะ"}
        </label>
      );
  };

  return (
    <>
      <Label
        style={{ backgroundColor: "var(--light-bg-color)", margin: "0 0 1rem", width: "35.5%" }}
        size="large"
      >
        ข้อมูลบัญชี
      </Label>
      <Form className="--override-disabled --grey">
        <Form.Group style={{ alignItems: "baseline" }} inline>
          <Form.Field width={3} required>
            <label style={{ fontWeight: "normal" }}>ชื่อ Official Account</label>
          </Form.Field>
          <FormInputEditable
            message="กรุณาระบุชื่อ Official Account"
            name="name"
            readOnly={props.readOnly}
            value={props.data.account_name || props.data.name}
            required
            onChangeValue={props.onChangeValue}
          />
        </Form.Group>

        {!props.readOnly && (
          <Form.Group inline>
            <Form.Field width={3}>
              ประเภทธุรกิจ <span style={{ color: "red" }}>*</span>
            </Form.Field>
            <Form.Field width={8}>
              <div style={{ display: "grid", width: "100%" }}>
                <div style={{ width: "100%" }}>
                  <Dropdown
                    style={{ width: "100%" }}
                    name="hospital_name"
                    placeholder="-- กรุณาเลือกประเภทธุรกิจ --"
                    value={props.data.hospital_name}
                    fluid
                    search
                    selection
                    options={props.healthFacilityTypeOptions}
                    onChange={props.onChangeValue}
                    error={props.error?.hospital_name}
                  />
                </div>
                <div>{requiredTextError?.(props.error?.hospital_name)}</div>
              </div>
            </Form.Field>
          </Form.Group>
        )}

        {/* {!props.readOnly && (
          <Form.Group inline>
            <Form.Field width={3}>ตำแหน่งสถานที่ตั้ง</Form.Field>
            <FormInputEditable
              name="address"
              value={props.data.address}
              onChangeValue={props.onChangeValue}
            />
          </Form.Group>
        )} */}
        {!props.readOnly && (
          <BusinessInformationForm
            detail={props.data}
            readOnly={props.readOnly ?? false}
            handleChangeValue={props.onChangeValue}
            error={props.error}
            width={3}
            businessTypeWidth={11}
            requiredTextError={requiredTextError}
            languageUX={props.languageUX}
          />
        )}
        {!props.readOnly && (
          <OfficialAccountAddressForm
            data={props.data}
            width={3}
            error={props.error}
            masterData={props.masterData}
            masterOptions={props.masterOptions}
            onChangeValue={props.onChangeValue}
            requiredTextError={requiredTextError}
            languageUX={props.languageUX}
          />
        )}

        {!props.readOnly && (
          <Form.Group inline>
            <Form.Field width={3}>รายละเอียดเพิ่มเติม</Form.Field>
            <FormInputEditable
              name="detail"
              value={props.data.detail}
              onChangeValue={props.onChangeValue}
            />
          </Form.Group>
        )}
      </Form>

      <Label
        style={{ backgroundColor: "var(--light-bg-color)", margin: "1.25rem 0 1rem", width: "35.5%" }}
        size="large"
      >
        ข้อมูลสำหรับออกใบกำกับภาษี
      </Label>
      <Form className="--override-disabled --grey">
        {!props.readOnly && (
          <AccountingInformationForm
            detail={props.data}
            readOnly={props.readOnly ?? false}
            handleChangeValue={props.onChangeValue}
            error={props.error}
            width={3}
            businessTypeWidth={11}
            requiredTextError={requiredTextError}
            languageUX={props.languageUX}
          />
        )}

        {!props.readOnly && (
          <AccountingAddressForm
            data={props.data}
            width={3}
            error={props.error}
            masterData={props.masterData}
            masterOptions={props.masterOptions}
            onChangeValue={props.onChangeValue}
            onChangeLogoImage={props.onChangeLogoImage}
            requiredTextError={requiredTextError}
            languageUX={props.languageUX}
          />
        )}
      </Form>

      <Label
        style={{
          backgroundColor: "var(--light-bg-color)",
          margin: "1.25rem 0 1rem",
          width: "35.5%",
        }}
        size="large"
      >
        Link สำหรับเข้าใช้งาน
      </Label>
      <Form className="--override-disabled --grey">
        <Form.Group style={{ alignItems: "baseline" }} inline>
          <Form.Field style={{ minWidth: "max-content" }} width={3}>
            Official Account Link [สำหรับส่งให้ผู้รับบริการ]
          </Form.Field>
          <Form.Field width={9}>
            <div style={{ width: "100%" }}>
              <div style={{ display: "flex" }}>
                <Input
                  ref={inputRef}
                  style={{ flex: 1, marginRight: "10px" }}
                  value={props.data.account_link}
                  readOnly
                />
                <Button
                  style={{
                    backgroundColor: isCopied ? "#10b981" : "var(--primary-theme-color)",
                    color: "white",
                    minWidth: "max-content",
                    transition: "background-color 0.2s ease",
                  }}
                  icon
                  onClick={handleCopyClipboard}
                >
                  <Icon name={isCopied ? "check" : "copy"} />
                  <label style={{ marginLeft: "6px" }}>
                    {isCopied ? "คัดลอกแล้ว" : "คัดลอก Link"}
                  </label>
                </Button>
              </div>
              <div style={{ color: "#666", fontSize: "14px", marginTop: "15px" }}>
                ท่านสามารถคัดลอกและส่ง Link นี้ให้กับผู้รับบริการในเครือของท่าน เพื่อเชิญเข้าใช้งาน
              </div>

              <div ref={qrRef} style={{ marginTop: "1rem" }}>
                <QRCode size={QR_SIZE} value={props.data.account_link || ""} />
              </div>
              <div>
                <Button
                  style={{
                    backgroundColor: "var(--primary-theme-color)",
                    color: "white",
                    marginTop: "0.25rem",
                    minWidth: "max-content",
                  }}
                  size="mini"
                  icon
                  onClick={downloadQR}
                >
                  <Icon name="download" />
                  บันทึก QR Code
                </Button>
              </div>
            </div>
          </Form.Field>
        </Form.Group>
      </Form>
    </>
  );
};

/* ====================================================== */
/*                    FormInputEditable                   */
/* ====================================================== */
type FormInputEditableProps = {
  message?: string;
  name: string;
  readOnly?: boolean;
  required?: boolean;
  value: string;
  onChangeValue?: (e: any, data: any) => void;
};

const FormInputEditable = (props: FormInputEditableProps) => {
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [inputValue, setInputValue] = useState<string>("");
  const [errMsg, setErrMsg] = useState<boolean | string>(false);

  const inputRef = useRef<any>(null);

  useEffect(() => {
    setInputValue(props.value);
  }, [props.value]);

  useEffect(() => {
    if (props.readOnly) {
      setIsEditing(true);
    }
  }, [props.readOnly]);

  // Focus input when editing starts (and not in readonly mode)
  useEffect(() => {
    if (isEditing && !props.readOnly && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing, props.readOnly]);

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleSubmit = () => {
    if (props.required) {
      if (!inputValue) {
        setErrMsg(true);

        return;
      } else if (inputValue.startsWith("@")) {
        setErrMsg("ไม่สามารถใช้ @ นำหน้าชื่อได้");

        return;
      }
    }

    setErrMsg(false);
    setIsEditing(false);

    props.onChangeValue?.(null, { name: props.name, value: inputValue });
  };

  return (
    <>
      <Form.Field width={isEditing ? 8 : undefined}>
        {isEditing ? (
          <div style={{ textAlign: "right", width: "100%" }}>
            <Input
              ref={inputRef}
              placeholder="กรุณาระบุชื่อ"
              readOnly={props.readOnly}
              value={inputValue}
              onChange={(e, data) => {
                setInputValue(data.value);
              }}
            />
            {!!errMsg && (
              <label className="font-error" style={{ color: "red", fontSize: "12px" }}>
                {typeof errMsg === "string" ? errMsg : props.message}
              </label>
            )}
          </div>
        ) : (
          <>{inputValue || "-"}</>
        )}
      </Form.Field>
      {!props.readOnly && (
        <Form.Field width={4}>
          {isEditing ? (
            <Button
              style={{ backgroundColor: "var(--primary-theme-color)", color: "white" }}
              onClick={handleSubmit}
            >
              ตกลง
            </Button>
          ) : (
            <div>
              <img
                style={{
                  cursor: "pointer",
                  width: "20px",
                  ...(props.required && { marginBottom: "-2px" }),
                }}
                alt="pencil-edit.icon"
                src={IMAGES.pencil}
                onClick={handleEdit}
              />
            </div>
          )}
        </Form.Field>
      )}
    </>
  );
};

export default SettingOAAccountFormDetailed;
